import type { Entity, QueriesObject, QueryResults, System } from '../../odie';

import { BasicHandler } from '../../lib/defs/types';
import { BlockComponent } from '../components/BlockComponent';
import { PhaseComponent } from '../components/PhaseComponent';
import { MushroomBlockComponent } from '../components/MushroomBlockComponent';
import { config } from '../defs/config';
import { PhaseId } from '../defs/types';
import { MushroomBlockEntity } from '../entities/BlockEntity';
import type { GameScene } from '../GameScene';
import { matchFindGroups } from '../util/matchTools';
import { MushroomEffect } from '../actions/effects/MushroomEffect';

/*
    handles mushroom block specifics
*/
export class MushroomBlockSystem implements System {
  // constants
  //-------------------------------------------------------------------------
  public static readonly NAME = 'blockMushroom';
  public static Queries: QueriesObject = {
    block: {
      components: [BlockComponent, MushroomBlockComponent],
      removed: true,
    },
    phase: {
      components: [PhaseComponent],
      modified: true,
    },
  };

  // fields
  //-------------------------------------------------------------------------
  // injected
  public scene!: GameScene;
  public queries!: QueryResults;
  //NOTE: state in system bad cuz ecs or something
  // state
  private _mushroomDestroyed = false;
  // maps
  private _phaseHandlers: { [key in PhaseId]?: BasicHandler } = {
    input: this._onInputPhase,
  };

  // impl
  //-------------------------------------------------------------------------
  public modifiedQuery(
    entity: Entity,
    component: PhaseComponent,
    properties: { phase?: PhaseId },
  ) {
    // handle phase change
    if (properties.phase) this._phaseHandlers[component.phase]?.call(this);
  }

  public removedFromQuery() {
    // set mushroom destroyed state
    this._mushroomDestroyed = true;
  }

  // private: events
  //-------------------------------------------------------------------------
  private _onInputPhase() {
    const phase = this.scene.sessionEntity.c.phase;

    // if move input and after first round
    if (phase.moved && !this._mushroomDestroyed && phase.round > 0) {
      this._activateMushroom();
    }

    // reset destroyed state
    this._mushroomDestroyed = false;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private _activateMushroom() {
    const mushrooms: MushroomBlockEntity[] = [];

    // collect mushrooms
    this.queries.block.forEach((entity: MushroomBlockEntity) =>
      mushrooms.push(entity),
    );

    // if mushrooms found
    if (mushrooms.length > 0) {
      // get match groups
      const matchGroups = matchFindGroups(this.scene);
      const singleMatchGroup = matchGroups.length === 1;
      const getMatchableCount = () => {
        if (!matchGroups.length) return 0;
        return matchGroups[0].matches.reduce(
          (count, match) => count + (match.overlay ? 0 : 1),
          0,
        );
      };

      // require multiple match groups exist or the last one has matchable count excluding overlayed blocks
      if (
        !singleMatchGroup ||
        getMatchableCount() > config.blocks.basic.match
      ) {
        // execute mushroom effect
        new MushroomEffect(this.scene, { mushrooms }).execute();

        return true;
      }
    }

    return false;
  }
}
